// src/pages/PreferencesPage.tsx -- Slice L / P5; tone selector + Dark Mode
// 2026-11-04.
//
// Dedicated screen for app preferences (skin). Matches the pattern of
// /me/details and /me/privacy: tap a card on /me, navigate here, save
// explicitly, hard-reload back to home so theme changes apply globally.
//
// Skins: three light tones (Pale Yellow = the original warm-gold palette,
// Pale Blue, Pale Green), Dark Mode (iPhone-style), and the NYMR Website
// palette. Picking a swatch previews it instantly on this page; Save persists
// it (PUT /api/me/preferences { theme }). Cancel restores the saved skin.
//
// The old "Home icon" choice (art-deco train / NYMR logo) is gone: the Home
// banner now always shows the NYMR logo.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { useCurrentUser, type ThemeKey } from "../auth";
import { THEMES, THEME_KEYS, THEME_LABELS, DEFAULT_THEME, applyThemeToDocument, isThemeKey } from "../lib/themes";
import { C, F } from "../theme";
import { Button, Card, Eyebrow, Icons, TopBar } from "../components/ui";

const THEME_BLURB: Readonly<Record<ThemeKey, string>> = {
  "warm-gold":    "The heritage cream and brass look.",
  "pale-blue":    "Cool pale-blue surfaces, navy accents.",
  "pale-green":   "Soft pale-green surfaces, moss accents.",
  dark:           "Near-black, dark grey cards, blue accents.",
  "nymr-website": "The nymr.co.uk colours.",
};

export function PreferencesPage() {
  const navigate = useNavigate();
  const { me } = useCurrentUser();

  const savedTheme: ThemeKey = isThemeKey(me?.preferences?.theme) ? me!.preferences!.theme! : DEFAULT_THEME;

  const [theme, setTheme] = useState<ThemeKey>(savedTheme);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Sync only when the SERVER preference changes (first /api/me landing);
    // never clobber an unsaved pick.
    setTheme(savedTheme);
  }, [savedTheme]);

  // Live preview: the chosen skin paints the whole document while on this
  // page; leaving without saving restores the saved one. The preview waits
  // for /api/me (otherwise the default skin would flash before the saved one
  // is known), and the unmount restore reads the LATEST saved skin through a
  // ref -- a closure over `savedTheme` captured the pre-/api/me default and
  // reset Dark Mode users to Pale Yellow on Back (2026-11-04 fix).
  const savedRef = useRef<ThemeKey>(savedTheme);
  useEffect(() => {
    savedRef.current = savedTheme;
  }, [savedTheme]);
  useEffect(() => {
    if (!me) return;
    applyThemeToDocument(theme);
  }, [theme, me]);
  useEffect(() => {
    return () => applyThemeToDocument(savedRef.current);
  }, []);

  const dirty = theme !== savedTheme;

  async function save() {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await apiFetch("/api/me/preferences", {
        method: "PUT",
        body: JSON.stringify({ theme }),
      });
      // Hard reload to /me so the theme applies globally without having to
      // plumb refresh() through every consumer of useCurrentUser.
      window.location.replace("/me");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not save preferences");
      setSaving(false);
    }
  }

  return (
    <div>
      <TopBar title="App preferences" sub="Skin and tone" onBack={() => navigate("/me")} />
      <div style={{ padding: "14px 16px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
        <Card>
          <Eyebrow>Skin &amp; tone</Eyebrow>
          <p
            style={{
              fontFamily: F.body,
              fontSize: 13,
              color: C.inkSoft,
              fontWeight: 600,
              margin: "8px 0 12px",
              lineHeight: 1.4,
            }}
          >
            Pick the tone the hub renders in. It previews straight away; Save keeps it on every device you sign in on.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {THEME_KEYS.map((key) => {
              const active = theme === key;
              const t = THEMES[key];
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTheme(key)}
                  aria-pressed={active}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 8,
                    padding: 12,
                    minHeight: 112,
                    borderRadius: 16,
                    border: `2px solid ${active ? C.brassDk : C.hairline}`,
                    background: C.card,
                    color: C.ink,
                    fontFamily: F.body,
                    cursor: "pointer",
                    textAlign: "left",
                    WebkitTapHighlightColor: "transparent",
                    touchAction: "manipulation",
                    boxShadow: active ? C.shadowM : "none",
                  }}
                >
                  {/* Swatch: the skin's page, card and accent at a glance */}
                  <div
                    aria-hidden="true"
                    style={{
                      width: "100%",
                      height: 40,
                      borderRadius: 10,
                      background: t.bg,
                      border: `1px solid ${t.hairline}`,
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <div style={{ position: "absolute", left: 8, top: 8, right: 8, bottom: 8, borderRadius: 6, background: t.card, border: `1px solid ${t.hairlineLt}` }} />
                    <div style={{ position: "absolute", left: 14, top: 14, width: 12, height: 12, borderRadius: 12, background: t.accent }} />
                    <div style={{ position: "absolute", left: 32, top: 16, width: 46, height: 4, borderRadius: 4, background: t.ink, opacity: 0.8 }} />
                    <div style={{ position: "absolute", left: 32, top: 23, width: 30, height: 3, borderRadius: 3, background: t.inkSoft, opacity: 0.6 }} />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, width: "100%" }}>
                    <span style={{ fontWeight: 800, fontSize: 14, flex: 1 }}>{THEME_LABELS[key]}</span>
                    {active && <Icons.Check s={16} c={C.brassDk} sw={2.4} />}
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: C.inkSoft, lineHeight: 1.3 }}>{THEME_BLURB[key]}</span>
                </button>
              );
            })}
          </div>
        </Card>

        {error && (
          <div
            role="alert"
            style={{
              padding: 12,
              borderRadius: 12,
              background: C.signalBg,
              border: `1px solid rgba(184,58,43,0.22)`,
              color: C.signalDk,
              fontFamily: F.body,
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
          <Button
            variant="primary"
            fullWidth
            disabled={saving || !dirty}
            onClick={save}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
          <Button
            variant="ghost"
            disabled={saving}
            onClick={() => navigate("/me")}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
